import { formatMonthLabel, monthKey } from '@/shared/lib/dates';
import type { ProjectRowView } from '@/shared/types/domain';

/**
 * O agrupamento da tabela de projetos: um bloco por mês de execução.
 *
 * O eixo da listagem é a DATA, e não a situação — a situação já é uma coluna da
 * própria linha. Gastar também a estrutura da tela com status diria a mesma
 * coisa duas vezes e deixaria "quando isso acontece?" sem resposta, que é
 * justamente o que planejar e cobrar exigem.
 *
 * ⚠️ **Houve um grampeamento do mês passado no mês corrente, e ele foi removido
 * de propósito.** Enquanto a tela era partida em "ativos" e "arquivados", o
 * bloco de ativos prometia "deste mês em diante", e abrir um cabeçalho de julho
 * ali dentro contradiria a estrutura logo na primeira linha — então tudo que
 * começou antes era puxado para o mês corrente. A tabela contínua desfez essa
 * promessa: ela carrega concluído, rejeitado e cancelado no mesmo corpo, e um
 * projeto encerrado em março de 2026 aparecer sob "agosto de 2026" seria
 * simplesmente falso. Mês real, sempre.
 *
 * Quem sustenta a visibilidade do que pede ação passou a ser `splitPastMonths`:
 * o mês corrente é onde a tabela abre, e o vencido sem decisão cai nele ou logo
 * acima, no primeiro bloco que o botão de histórico revela.
 */
/**
 * Genérico no item, e não fixo em `ProjectWithContext`: o calendário lista
 * linhas vindas da projeção reduzida de `agenda_projects` (spec-calendar.md
 * §3.3), que não é a view inteira. O agrupamento só precisa de `starts_at` —
 * pedir mais do que se usa obrigaria quem chama a fabricar colunas.
 */
export interface ProjectMonthGroup<T = ProjectRowView> {
  /** `2026-09`, ou `sem-data`. Chave de React e de ordenação, nunca exibida. */
  key: string;
  label: string;
  items: readonly T[];
}

/** Chave do balde de quem ainda não marcou data. */
export const UNDATED = 'sem-data';

/**
 * Os projetos agrupados por mês de execução, em ordem crescente, e ordenados
 * por data dentro de cada mês.
 *
 * A consulta já devolve por `starts_at` crescente, mas a ordenação é refeita
 * aqui: esta função também atende telas que montam a lista de outra origem, e
 * um agrupamento que só funciona se quem chamou lembrou de ordenar antes é uma
 * armadilha silenciosa — o sintoma seria uma linha fora de ordem no meio do
 * mês, que ninguém reporta como bug.
 *
 * Projeto sem data vai para um balde próprio, SEMPRE por último: ele não tem
 * lugar na cronologia, mas sumir da tela seria pior — é justamente o projeto
 * que alguém precisa lembrar de datar.
 */
export function groupProjectsByMonth<T extends Pick<ProjectRowView, 'starts_at'>>(
  projects: readonly T[],
): readonly ProjectMonthGroup<T>[] {
  const buckets = new Map<string, { label: string; items: T[] }>();

  for (const project of projects) {
    const key = project.starts_at ? monthKey(project.starts_at) : UNDATED;
    const label =
      key === UNDATED ? 'Sem data definida' : formatMonthLabel(project.starts_at as string);

    const bucket = buckets.get(key);
    if (bucket) bucket.items.push(project);
    else buckets.set(key, { label, items: [project] });
  }

  return [...buckets.entries()]
    .sort(([a], [b]) => {
      if (a === UNDATED) return 1;
      if (b === UNDATED) return -1;
      // `2026-09` ordena por texto igual ao que ordena por tempo — é o motivo
      // de a chave ser `yyyy-mm` e não `setembro de 2026`.
      return a.localeCompare(b);
    })
    .map(([key, { label, items }]) => ({ key, label, items: sortByStart(items) }));
}

/** Crescente por início; sem data por último, para o balde `sem-data` ficar estável. */
function sortByStart<T extends Pick<ProjectRowView, 'starts_at'>>(
  items: readonly T[],
): readonly T[] {
  return [...items].sort((a, b) => {
    if (!a.starts_at) return b.starts_at ? 1 : 0;
    if (!b.starts_at) return -1;
    return Date.parse(a.starts_at) - Date.parse(b.starts_at);
  });
}

export interface SplitMonths<T = ProjectRowView> {
  /** Meses inteiramente anteriores ao corrente — o histórico, recolhido. */
  past: readonly ProjectMonthGroup<T>[];
  /** Do mês corrente em diante. É por onde a tabela abre. */
  current: readonly ProjectMonthGroup<T>[];
}

/**
 * Onde a tabela abre.
 *
 * Uma listagem que inclui o arquivo inteiro não pode começar no projeto
 * concluído mais antigo: quem abre `/projetos` pergunta "o que eu tenho pela
 * frente?", e responder isso exigiria rolar por anos de histórico toda vez. Os
 * meses anteriores continuam na mesma tabela, na mesma ordem crescente — só
 * ficam atrás de um clique.
 *
 * ⚠️ **`sem-data` acompanha `current`, e não o histórico.** Projeto sem data é
 * exatamente aquele que alguém precisa lembrar de datar; enterrá-lo atrás do
 * botão de histórico o transformaria no único item da lista que ninguém
 * encontra sem procurar.
 */
export function splitPastMonths<T>(
  groups: readonly ProjectMonthGroup<T>[],
  now: Date = new Date(),
): SplitMonths<T> {
  const currentKey = monthKey(now);

  return {
    past: groups.filter((group) => group.key !== UNDATED && group.key < currentKey),
    current: groups.filter((group) => group.key === UNDATED || group.key >= currentKey),
  };
}
